import {
  createPyloPermission,
  getPyloAcos,
  getPyloPermissions,
  PyloAro,
  updatePyloPermission,
} from "@/services/acl.service";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEntityInstanceById } from "../use-entity-instance-by-id";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { getInstanceDisplayValue } from "@/lib/utils";

export const useAcl = () => {
  const queryClient = useQueryClient();

  // Middleware guarantees auth
  const { data: pyloAcos } = useQuery({
    queryKey: ["pyloAcos"],
    queryFn: getPyloAcos,
  });

  const { mutate: createPyloPermissionMutation } = useMutation({
    mutationFn: createPyloPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pyloPermissions"] });
    },
  });

  const { mutate: updatePyloPermissionMutation } = useMutation({
    mutationFn: updatePyloPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pyloPermissions"] });
    },
  });

  return {
    pyloAcos,
    createPyloPermission: createPyloPermissionMutation,
    updatePyloPermission: updatePyloPermissionMutation,
  };
};

export const usePyloPermissions = ({
  aroId,
  acoId,
  acoIds,
}: {
  aroId?: string;
  acoId?: string;
  /** When set, match any of these ACOs (the selected ACO + its ancestors). */
  acoIds?: string[];
}) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: ["pyloPermissions", aroId, acoId, acoIds],
    queryFn: ({ pageParam = 1 }) => getPyloPermissions({ aroId, acoId, acoIds, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!aroId || !!acoId || !!acoIds?.length,
  });

  return infiniteQuery;
};

export const useAro = (item?: PyloAro) => {
  const entityName = item?.entity?.data.name ?? "";

  // Resolve which field represents an instance via the shared layout store: it
  // seeds a sensible default (the `name`-like heuristic in `getDefaultField`) and
  // is the same store the display-field picker writes to, so an admin override
  // applies everywhere this entity is displayed. The field may be a deep to-one
  // relation path (e.g. `customer.region.name`).
  const { state: layoutState } = useEntityInstanceLayoutStore(entityName);
  const displayField = layoutState?.field ?? "";

  const { data, isLoading } = useEntityInstanceById({
    entityName,
    id: item?.instance_id ?? "",
    fieldsToFetch: displayField ? ["id", displayField] : ["id"],
  });

  const fieldValue = displayField ? getInstanceDisplayValue(data?.data, displayField) : undefined;

  return {
    // With an instance id, prefer the instance's display-field value; fall back to
    // the entity (type) name only when there's no instance or it hasn't resolved.
    displayName: item?.instance_id && fieldValue ? String(fieldValue) : entityName,
    isLoading,
  };
};
